import React from "react";
import {Controller, useForm, SubmitHandler, FormProvider} from "react-hook-form";
import MWCaption from "../../components/MWCaption";

import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  TextareaAutosize,
  Typography,
} from "@mui/material";

import ConfirmButtonGroup from "../../components/ConfirmButtonGroup";
import SubTitle from "../../components/SubTitle";
import MWProgress from "../../components/MWProgress";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {tokenSelector} from "../../Recoil/Atom/authState";
import {useUpdateMutation} from "../../hooks/useQueryHooks";
import * as Params from "../../models/clientModels/interfaces/apiParams";
import * as MaintenanceApi from "../../api/workOrderApi/MaintenanceApi";

import {ResponseItem} from "../../models/clientModels/interfaces/Response";
import {EQWorkOrder, initializeWorkOrder} from "../../models/serverModels/workOrder/EQWorkOrder";
import {useQueryClient} from "react-query";
import {snackBarState, snackBarToggleState} from "../../Recoil/Atom/snackBarState";
import {useLocation, useNavigate} from "react-router-dom";
import {StyledDetailContainer, StyledDetailTable} from "../../styledComponents/StyledDetail";
import MWInputDate from "../../components/MWInputDate";
import MWInputFile from "../../components/MWInputFile";
import DomainVerificationIcon from "@mui/icons-material/DomainVerification";

const WorkOrderComplete = () => {
  const tokenState = useRecoilValue(tokenSelector);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {state} = useLocation();

  const setSnackBarState = useSetRecoilState(snackBarState);
  const setSnackBarToggle = useSetRecoilState(snackBarToggleState);

  const {mutate: updateMutate, isLoading: isUpdateLoading} = useUpdateMutation<
    ResponseItem<EQWorkOrder>,
    Params.Update<EQWorkOrder>
  >({
    api: MaintenanceApi.completeWorkOrder,
    options: {
      onSuccess: () => {
        setSnackBarToggle(true);
        setSnackBarState("update");
        navigate(-1);
        queryClient.invalidateQueries("MaintenanceList");
      },
    },
  });

  const methods = useForm<EQWorkOrder>({defaultValues: initializeWorkOrder});

  const {control, handleSubmit} = methods;

  const onSubmit: SubmitHandler<EQWorkOrder> = (data) => {
    updateMutate({
      token: tokenState,
      item: {...initializeWorkOrder, ...data, uid: state.uid},
    });
  };
  return (
    <React.Fragment>
      <SubTitle subTitle="설비 점검 진행" />
      {isUpdateLoading && <MWProgress />}
      <StyledDetailContainer>
        <MWCaption title={"설비 점검 이력"} icon={<DomainVerificationIcon />} />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledDetailTable>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" width="10%">
                      작업위험성평가
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <MWInputDate name="evaluation2" />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      작업자교육
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <MWInputDate name="education1" />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      근로자교육
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <MWInputDate name="education2" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" width="10%">
                      작업위험성평가 자료
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <MWInputFile file="evaluation2FileBase64" fileName="evaluation2File" />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      작업자교육 자료
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <MWInputFile file="education1FileBase64" fileName="education1File" />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      근로자교육 자료
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <MWInputFile file="education2FileBase64" fileName="education2File" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th">작업허가서-화기</TableCell>
                    <TableCell component="td" colSpan={2}>
                      <MWInputFile file="fireWorkFileBase64" fileName="fireWorkFile" />
                    </TableCell>
                    <TableCell component="th">작업허가서-일반</TableCell>
                    <TableCell component="td" colSpan={2}>
                      <MWInputFile file="generalFileBase64" fileName="generalFile" />
                    </TableCell>
                    <TableCell component="th"></TableCell>
                    <TableCell component="td"></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" width="10%">
                      작업허가서-고소
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <Controller
                        name="isHeight"
                        control={control}
                        render={({field}) => (
                          <Checkbox
                            sx={{margin: 0, padding: 0}}
                            id="isHeight"
                            onChange={(e) => field.onChange(e.target.checked)}
                            checked={field.value}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      작업허가서-밀폐
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <Controller
                        name="isClosed"
                        control={control}
                        render={({field}) => (
                          <Checkbox
                            sx={{margin: 0, padding: 0}}
                            id="isClosed"
                            onChange={(e) => field.onChange(e.target.checked)}
                            checked={field.value}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      작업허가서-전기
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <Controller
                        name="isStopElectricity"
                        control={control}
                        render={({field}) => (
                          <Checkbox
                            sx={{margin: 0, padding: 0}}
                            id="isStopElectricity"
                            onChange={(e) => field.onChange(e.target.checked)}
                            checked={field.value}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" width="10%">
                      작업허가서-굴착
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <Controller
                        name="isExcavation"
                        control={control}
                        render={({field}) => (
                          <Checkbox
                            sx={{margin: 0, padding: 0}}
                            id="isExcavation"
                            onChange={(e) => field.onChange(e.target.checked)}
                            checked={field.value}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      작업허가서-중장비
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <Controller
                        name="isHeavy"
                        control={control}
                        render={({field}) => (
                          <Checkbox
                            sx={{margin: 0, padding: 0}}
                            id="isHeavy"
                            onChange={(e) => field.onChange(e.target.checked)}
                            checked={field.value}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      작업허가서-방사선
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <Controller
                        name="isRadiation"
                        control={control}
                        render={({field}) => (
                          <Checkbox
                            sx={{margin: 0, padding: 0}}
                            id="isRadiation"
                            onChange={(e) => field.onChange(e.target.checked)}
                            checked={field.value}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" width="10%">
                      시작일
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="13.33%">
                      <MWInputDate name="startDate" />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      종료일
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="13.33%">
                      <MWInputDate name="completeDate" />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      소요시간
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="13.33%">
                      <Controller
                        name="workedHours"
                        control={control}
                        render={({field, fieldState: {error}}) => (
                          <Box>
                            <Box sx={{display: "flex", alignItems: "flex-end"}}>
                              <TextField
                                {...field}
                                id="workedHours"
                                type="number"
                                size="small"
                                dir="rtl"
                                sx={{marginRight: "8px"}}
                              />
                              <Typography sx={{width: "48px"}}> 시간</Typography>
                            </Box>

                            <Typography color="secondary">{error?.message}</Typography>
                          </Box>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" width="10%">
                      소요금액(천원단위)
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="13.33%">
                      <Controller
                        name="cost"
                        control={control}
                        render={({field, fieldState: {error}}) => (
                          <Box>
                            <Box sx={{display: "flex", alignItems: "flex-end"}}>
                              <TextField
                                {...field}
                                id="cost"
                                type="number"
                                size="small"
                                dir="rtl"
                                sx={{marginRight: "8px"}}
                              />
                              <Typography sx={{width: "68px"}}> ,000원</Typography>
                            </Box>

                            <Typography color="secondary">{error?.message}</Typography>
                          </Box>
                        )}
                      />
                    </TableCell>
                    <TableCell component="th" width="10%">
                      가동전 점검
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <MWInputFile file="preCheckFileBase64" fileName="preCheckFile" />
                    </TableCell>

                    <TableCell component="th" width="10%">
                      기타 자료등록
                    </TableCell>
                    <TableCell component="td" colSpan={2} width="23.33%">
                      <MWInputFile file="attachmentBase64" fileName="attachment" />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th">비고</TableCell>
                    <TableCell component="td" colSpan={8}>
                      <Controller
                        name="externalComment"
                        control={control}
                        render={({field: {onChange, value}}) => (
                          <TextareaAutosize
                            defaultValue={value}
                            aria-label="minimum height"
                            minRows={3}
                            onChange={onChange}
                            style={{width: "100%", padding: "8px"}}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </StyledDetailTable>
            <ConfirmButtonGroup
              link="/WorkOrder/List/Processed"
              cancelText="취소"
              confirmText="완료"
            />
          </form>
        </FormProvider>
      </StyledDetailContainer>
    </React.Fragment>
  );
};

export default WorkOrderComplete;
